// src/components/PolicyFormNew.js
import React, { useState, useEffect } from 'react';
import { Plus, X, Loader2, Info, Shield, Lock, Ban, ChevronRight, AlertTriangle } from 'lucide-react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Card from './Card';
import RowRestrictionBuilder from './RowRestrictionBuilder';

const PolicyFormNew = ({ policy = null, onSave, onCancel }) => {
  const [formData, setFormData] = useState({
    name: '',
    accessMethod: 'wrapper',
    tables: {
      allowed_fully: [],
      allowed_restricted: {},
      forbidden: []
    }
  });
  
  const [availableTables, setAvailableTables] = useState([]);
  const [fullyAllowedType, setFullyAllowedType] = useState('specific'); // 'all' or 'specific'
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRestricted, setExpandedRestricted] = useState({});
  const [conflicts, setConflicts] = useState({});

  // Helper functions for SQL formatting
  const isNumericType = (columnType) => ['INTEGER', 'INT64', 'FLOAT', 'FLOAT64', 'NUMERIC', 'BIGNUMERIC'].includes(columnType);
  const isBooleanType = (columnType) => columnType === 'BOOLEAN';

  const formatValueForSQL = (value, columnType, operator) => {
    if (!value && value !== 0) return "''";
    
    if (operator === 'IN') {
      const values = value.split(',').map(v => `'${v.trim()}'`).join(', ');
      return `(${values})`;
    }
    
    if (operator === 'LIKE') {
      return `'%${value}%'`;
    }

    if (isNumericType(columnType)) {
      return value.toString();
    }

    if (isBooleanType(columnType)) {
      return value === 'true' || value === true ? 'true' : 'false';
    }

    return `'${value}'`;
  };

  // Check for conflicts between access levels
  const checkConflicts = (tables) => {
    const newConflicts = {};
    
    // If using wildcard, restricted and forbidden are exceptions - no conflict
    const isWildcard = fullyAllowedType === 'all';
    
    // Check each table for conflicts
    availableTables.forEach(table => {
      const tableName = table.name;
      const inFully = tables.allowed_fully.includes(tableName);
      const inRestricted = tableName in tables.allowed_restricted;
      const inForbidden = tables.forbidden.includes(tableName);
      
      const conflictList = [];
      
      // Only check conflicts for specific tables mode
      if (!isWildcard) {
        if (inFully && inRestricted) {
          conflictList.push('Table is in both Full Access and Restricted Access');
        }
        if (inFully && inForbidden) {
          conflictList.push('Table is in both Full Access and No Access');
        }
      }
      
      // This conflict always applies - can't be both restricted AND forbidden
      if (inRestricted && inForbidden) {
        conflictList.push('Table is in both Restricted Access and No Access');
      }
      
      if (conflictList.length > 0) {
        newConflicts[tableName] = conflictList;
      }
    });
    
    setConflicts(newConflicts);
    return Object.keys(newConflicts).length === 0;
  };

  // Fetch available tables
  useEffect(() => {
    const fetchTables = async () => {
      try {
        setLoading(true);
        const configDoc = await getDoc(doc(db, 'appConfiguration', 'tables'));
        if (configDoc.exists()) {
          const tablesData = configDoc.data();
          const tables = Object.entries(tablesData)
            .filter(([_, value]) => 
              typeof value === 'object' && 
              value !== null && 
              !value.isMaterializedView && 
              !value.motherTable
            )
            .map(([name, data]) => ({
              name,
              id: data.id,
              schema: data.schema || [],
              defaultVisibility: data.defaultVisibility !== false // Default to true if not specified
            }));
          setAvailableTables(tables);
        }
      } catch (err) {
        console.error('Error fetching tables:', err);
        setError('Failed to fetch available tables');
      } finally {
        setLoading(false);
      }
    };

    fetchTables();
  }, []);

  // Initialize form when editing
  useEffect(() => {
    if (policy) {
      setFormData(policy);
      setFullyAllowedType(policy.tables?.allowed_fully === '*' ? 'all' : 'specific');
      // Expand all restricted tables when editing
      const expanded = {};
      Object.keys(policy.tables?.allowed_restricted || {}).forEach(key => {
        expanded[key] = true;
      });
      setExpandedRestricted(expanded);
      // Check for conflicts in existing policy
      checkConflicts(policy.tables || { allowed_fully: [], allowed_restricted: {}, forbidden: [] });
    }
  }, [policy]);

  // Check conflicts whenever tables change
  useEffect(() => {
    checkConflicts(formData.tables);
  }, [formData.tables]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate no conflicts
    const hasNoConflicts = checkConflicts(formData.tables);
    if (!hasNoConflicts) {
      setError('Please resolve table conflicts before saving. A table cannot be in multiple access levels.');
      return;
    }
    
    try {
      setSaving(true);
      setError(null);

      // Prepare final data
      const finalData = {
        ...formData,
        tables: {
          ...formData.tables,
          allowed_fully: fullyAllowedType === 'all' ? '*' : formData.tables.allowed_fully
        }
      };

      await onSave(finalData, policy?.id);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleFullyAllowedTable = (tableName) => {
    if (fullyAllowedType !== 'specific') return;
    
    // Only check conflicts in specific mode
    if (tableName in formData.tables.allowed_restricted) {
      setError(`Cannot add "${tableName}" to Full Access - it's already in Restricted Access. Remove it from there first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    if (formData.tables.forbidden.includes(tableName)) {
      setError(`Cannot add "${tableName}" to Full Access - it's already in No Access. Remove it from there first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      tables: {
        ...prev.tables,
        allowed_fully: prev.tables.allowed_fully.includes(tableName)
          ? prev.tables.allowed_fully.filter(t => t !== tableName)
          : [...prev.tables.allowed_fully, tableName]
      }
    }));
  };

  const toggleForbiddenTable = (tableName) => {
    // Check if table is in fully allowed or restricted
    if (formData.tables.allowed_fully.includes(tableName)) {
      setError(`Cannot add "${tableName}" to No Access - it's already in Full Access. Remove it from there first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    if (tableName in formData.tables.allowed_restricted) {
      setError(`Cannot add "${tableName}" to No Access - it's already in Restricted Access. Remove it from there first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      tables: {
        ...prev.tables,
        forbidden: prev.tables.forbidden.includes(tableName)
          ? prev.tables.forbidden.filter(t => t !== tableName)
          : [...prev.tables.forbidden, tableName]
      }
    }));
  };

  const addRestrictedTable = (tableName) => {
    // Only check conflict with forbidden (restricted can be exception to wildcard)
    if (fullyAllowedType === 'specific' && formData.tables.allowed_fully.includes(tableName)) {
      setError(`Cannot add "${tableName}" to Restricted Access - it's already in Full Access. Remove it from there first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    if (formData.tables.forbidden.includes(tableName)) {
      setError(`Cannot add "${tableName}" to Restricted Access - it's already in No Access. Remove it from there first.`);
      setTimeout(() => setError(null), 5000);
      return;
    }
    
    const table = availableTables.find(t => t.name === tableName);
    if (!table) return;
  
    setFormData(prev => ({
      ...prev,
      tables: {
        ...prev.tables,
        allowed_restricted: {
          ...prev.tables.allowed_restricted,
          [tableName]: {
            originalTable: table.id,
            allowedColumns: [],
            restrictions: [],
            rowRestriction: null
          }
        }
      }
    }));
    
    // Auto-expand the newly added table
    setExpandedRestricted(prev => ({ ...prev, [tableName]: true }));
  };

  const removeRestrictedTable = (tableName) => {
    setFormData(prev => {
      const newRestricted = { ...prev.tables.allowed_restricted };
      delete newRestricted[tableName];
      return {
        ...prev,
        tables: {
          ...prev.tables,
          allowed_restricted: newRestricted
        }
      };
    });
    
    setExpandedRestricted(prev => {
      const newExpanded = { ...prev };
      delete newExpanded[tableName];
      return newExpanded;
    });
  };

  const toggleRestrictedColumn = (tableName, columnName) => {
    setFormData(prev => {
      const table = prev.tables.allowed_restricted[tableName];
      const columns = table.allowedColumns || [];
      const newColumns = columns.includes(columnName)
        ? columns.filter(c => c !== columnName)
        : [...columns, columnName];

      return {
        ...prev,
        tables: {
          ...prev.tables,
          allowed_restricted: {
            ...prev.tables.allowed_restricted,
            [tableName]: {
              ...table,
              allowedColumns: newColumns
            }
          }
        }
      };
    });
  };

  const toggleAllRestrictedColumns = (tableName) => {
    const table = availableTables.find(t => t.name === tableName);
    if (table) {
      const allColumns = table.schema.map(field => field.name);
      const currentColumns = formData.tables.allowed_restricted[tableName]?.allowedColumns || [];
      
      setFormData(prev => ({
        ...prev,
        tables: {
          ...prev.tables,
          allowed_restricted: {
            ...prev.tables.allowed_restricted,
            [tableName]: {
              ...prev.tables.allowed_restricted[tableName],
              allowedColumns: currentColumns.length === allColumns.length ? [] : allColumns
            }
          }
        }
      }));
    }
  };

  // Helper to check if a table is already used in another access level
  const getTableStatus = (tableName) => {
    const inFully = formData.tables.allowed_fully.includes(tableName);
    const inRestricted = tableName in formData.tables.allowed_restricted;
    const inForbidden = formData.tables.forbidden.includes(tableName);
    
    if (inFully) return { level: 'full', color: 'green' };
    if (inRestricted) return { level: 'restricted', color: 'blue' };
    if (inForbidden) return { level: 'forbidden', color: 'red' };
    return null;
  };

  if (loading) {
    return (
      <Card>
        <div className="p-6 flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-gray-500" />
        </div>
      </Card>
    );
  }

  // Count public tables for display
  const publicTableCount = availableTables.filter(t => t.defaultVisibility).length;
  const hasConflicts = Object.keys(conflicts).length > 0;

  return (
    <Card>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">
            {policy ? 'Edit Wrapper Policy' : 'Create New Wrapper Policy'}
          </h2>
          <button
            type="button"
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Policy Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Policy Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
              placeholder="e.g., Sales Team Policy"
              required
            />
          </div>

          {/* Conflict Warning */}
          {hasConflicts && (
            <div className="bg-red-50 border border-red-200 rounded-md p-3">
              <div className="flex items-start space-x-2">
                <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm">
                  <p className="font-medium text-red-900">Table conflicts detected:</p>
                  <ul className="mt-1 space-y-1">
                    {Object.entries(conflicts).map(([table, issues]) => (
                      <li key={table} className="text-red-700">
                        <span className="font-medium">{table}:</span> {issues.join('; ')}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Access Control Sections */}
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Configure table access by setting permissions across three levels. Each table can only be in one level.
            </div>

            {/* Section 1: Full Access */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-5 w-5 text-green-600" />
                    <h3 className="font-medium text-gray-900">Full Access</h3>
                    <span className="text-sm text-gray-500">— Complete read permissions</span>
                  </div>
                </div>
              </div>
              
              <div className="p-4">
                <div className="space-y-3">
                  <div className="flex flex-col space-y-2">
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        value="all"
                        checked={fullyAllowedType === 'all'}
                        onChange={(e) => setFullyAllowedType(e.target.value)}
                        className="text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm">Grant access to all public tables ({publicTableCount} tables)</span>
                    </label>
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="radio"
                        value="specific"
                        checked={fullyAllowedType === 'specific'}
                        onChange={(e) => setFullyAllowedType(e.target.value)}
                        className="text-green-600 focus:ring-green-500"
                      />
                      <span className="text-sm">Select specific tables</span>
                    </label>
                  </div>

                  {fullyAllowedType === 'all' && (
                  <div className="space-y-2 mt-2">
                    <div className="bg-green-50 border border-green-200 rounded-md p-3">
                      <div className="flex items-start space-x-2">
                        <Info className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <div className="text-xs text-green-800">
                          <p>
                            Includes all tables marked as public ({publicTableCount} tables). 
                            Tables marked as sensitive require explicit permission and will never be included in wildcard access.
                          </p>
                          {(Object.keys(formData.tables.allowed_restricted).length > 0 || 
                            formData.tables.forbidden.length > 0) && (
                            <div className="mt-2 pt-2 border-t border-green-300">
                              <p className="font-medium mb-1">Exceptions to wildcard access:</p>
                              {Object.keys(formData.tables.allowed_restricted).length > 0 && (
                                <p className="ml-2">
                                  • <span className="text-blue-700">Restricted:</span> {Object.keys(formData.tables.allowed_restricted).join(', ')}
                                </p>
                              )}
                              {formData.tables.forbidden.length > 0 && (
                                <p className="ml-2">
                                  • <span className="text-red-700">No Access:</span> {formData.tables.forbidden.join(', ')}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                  {fullyAllowedType === 'specific' && (
                    <div className="bg-white border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                        {availableTables.map(table => {
                          const status = getTableStatus(table.name);
                          const isDisabled = status && status.level !== 'full';
                          
                          return (
                            <label
                              key={table.name}
                              className={`flex items-center space-x-2 p-2 rounded cursor-pointer ${
                                isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'
                              }`}
                              title={isDisabled ? `Already in ${status.level} access` : ''}
                            >
                              <input
                                type="checkbox"
                                checked={formData.tables.allowed_fully.includes(table.name)}
                                onChange={() => !isDisabled && toggleFullyAllowedTable(table.name)}
                                disabled={isDisabled}
                                className="rounded border-gray-300 text-green-600 focus:ring-green-500 disabled:cursor-not-allowed"
                              />
                              <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                                {table.name}
                              </span>
                              {!table.defaultVisibility && (
                                <Lock className="h-3 w-3 text-gray-400" title="Sensitive table" />
                              )}
                              {status && status.level !== 'full' && (
                                <span className={`text-xs text-${status.color}-600`}>
                                  ({status.level})
                                </span>
                              )}
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Section 2: Restricted Access */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Lock className="h-5 w-5 text-blue-600" />
                    <h3 className="font-medium text-gray-900">Restricted Access</h3>
                    <span className="text-sm text-gray-500">
                      — Limited columns or filtered rows
                      {fullyAllowedType === 'all' && ' (exceptions to wildcard)'}
                    </span>
                  </div>
                  <select
                    onChange={(e) => e.target.value && addRestrictedTable(e.target.value)}
                    value=""
                    className="text-sm px-3 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                  >
                    <option value="">+ Add Table</option>
                    {availableTables
                      .filter(t => {
                        // Only show tables not already in any access level
                        return !Object.keys(formData.tables.allowed_restricted).includes(t.name) &&
                               !formData.tables.allowed_fully.includes(t.name) &&
                               !formData.tables.forbidden.includes(t.name);
                      })
                      .map(table => (
                        <option key={table.name} value={table.name}>
                          {table.name}
                        </option>
                      ))}
                  </select>
                </div>
              </div>
              
              <div className="p-4">
                {Object.keys(formData.tables.allowed_restricted).length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">
                    No restricted tables configured. Click "+ Add Table" to add restrictions.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {Object.entries(formData.tables.allowed_restricted).map(([tableName, config]) => {
                      const tableSchema = availableTables.find(t => t.name === tableName);
                      const isExpanded = expandedRestricted[tableName];
                      const hasConflict = conflicts[tableName];
                      
                      return (
                        <div key={tableName} className={`border rounded-md overflow-hidden ${
                          hasConflict ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        }`}>
                          <div 
                            className={`px-3 py-2 cursor-pointer flex items-center justify-between ${
                              hasConflict ? 'bg-red-100 hover:bg-red-200' : 'bg-white hover:bg-gray-50'
                            }`}
                            onClick={() => setExpandedRestricted(prev => ({ ...prev, [tableName]: !prev[tableName] }))}
                          >
                            <div className="flex items-center space-x-2">
                              <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                              <span className={`font-medium ${hasConflict ? 'text-red-900' : 'text-gray-900'}`}>
                                {tableName}
                              </span>
                              {hasConflict && <AlertTriangle className="h-4 w-4 text-red-600" />}
                              <span className="text-xs text-gray-500">
                                ({config.allowedColumns.length} columns, {config.restrictions?.length || 0} filters)
                              </span>
                            </div>
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeRestrictedTable(tableName);
                              }}
                              className="text-red-500 hover:text-red-700"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          </div>

                          {isExpanded && (
                            <div className="border-t border-gray-200 p-3 bg-gray-50 space-y-3">
                              {/* Column Selection */}
                              <div>
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-sm font-medium text-gray-700">
                                    Allowed Columns
                                  </label>
                                  <button
                                    type="button"
                                    onClick={() => toggleAllRestrictedColumns(tableName)}
                                    className="text-xs text-blue-600 hover:text-blue-700"
                                  >
                                    {tableSchema && config.allowedColumns.length === tableSchema.schema.length
                                      ? 'Deselect All'
                                      : 'Select All'}
                                  </button>
                                </div>
                                <div className="bg-white border border-gray-200 rounded p-2 max-h-32 overflow-y-auto">
                                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                                    {tableSchema?.schema.map(field => (
                                      <label
                                        key={field.name}
                                        className="flex items-center space-x-1 p-1 text-xs cursor-pointer hover:bg-gray-50 rounded"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={config.allowedColumns.includes(field.name)}
                                          onChange={() => toggleRestrictedColumn(tableName, field.name)}
                                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <span>{field.name}</span>
                                      </label>
                                    ))}
                                  </div>
                                </div>
                              </div>

                              {/* Row Filters */}
                              <div>
                                <RowRestrictionBuilder
                                  columns={tableSchema?.schema || []}
                                  allowedColumns={config.allowedColumns || []}
                                  restrictions={config.restrictions || []}
                                  onChange={(newRestrictions) => {
                                    const rowRestriction = newRestrictions
                                      .map(({ column, operator, value, columnType }) => {
                                        if (operator === 'IN') {
                                          const values = value.split(',').map(v => `'${v.trim()}'`).join(', ');
                                          return `${column} IN (${values})`;
                                        } else if (operator === 'LIKE') {
                                          return `${column} LIKE '%${value}%'`;
                                        } else {
                                          const formattedValue = formatValueForSQL(value, columnType, operator);
                                          return `${column} ${operator} ${formattedValue}`;
                                        }
                                      })
                                      .join(' AND ');
                                  
                                    setFormData(prev => ({
                                      ...prev,
                                      tables: {
                                        ...prev.tables,
                                        allowed_restricted: {
                                          ...prev.tables.allowed_restricted,
                                          [tableName]: {
                                            ...prev.tables.allowed_restricted[tableName],
                                            restrictions: newRestrictions,
                                            rowRestriction: rowRestriction || null
                                          }
                                        }
                                      }
                                    }));
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Forbidden */}
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center space-x-2">
                  <Ban className="h-5 w-5 text-red-600" />
                  <h3 className="font-medium text-gray-900">No Access</h3>
                  <span className="text-sm text-gray-500">
                    — Explicitly denied tables
                    {fullyAllowedType === 'all' && ' (exceptions to wildcard)'}
                  </span>
                </div>
              </div>
              
              <div className="p-4">
                <div className="bg-white border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto">
                  {availableTables.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-2">No tables available</p>
                  ) : (
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-1">
                      {availableTables.map(table => {
                        const status = getTableStatus(table.name);
                        const isDisabled = status && status.level !== 'forbidden';
                        
                        return (
                          <label
                            key={table.name}
                            className={`flex items-center space-x-2 p-2 rounded cursor-pointer ${
                              isDisabled ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-gray-50'
                            }`}
                            title={isDisabled ? `Already in ${status.level} access` : ''}
                          >
                            <input
                              type="checkbox"
                              checked={formData.tables.forbidden.includes(table.name)}
                              onChange={() => !isDisabled && toggleForbiddenTable(table.name)}
                              disabled={isDisabled}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-500 disabled:cursor-not-allowed"
                            />
                            <span className={`text-sm ${isDisabled ? 'text-gray-400' : 'text-gray-700'}`}>
                              {table.name}
                            </span>
                            {!table.defaultVisibility && (
                              <Lock className="h-3 w-3 text-gray-400" title="Sensitive table" />
                            )}
                            {status && status.level !== 'forbidden' && (
                              <span className={`text-xs text-${status.color}-600`}>
                                ({status.level})
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Note: According to the design, forbidden tables technically override other permissions, 
                  but for clarity we prevent selecting the same table in multiple levels.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3 flex items-start space-x-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.name || hasConflicts}
              className="inline-flex items-center px-4 py-2 text-sm text-white bg-primary rounded-md hover:bg-primary/90 disabled:bg-gray-400 disabled:cursor-not-allowed"
              title={hasConflicts ? "Please resolve conflicts before saving" : ""}
            >
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                policy ? 'Update Policy' : 'Create Policy'
              )}
            </button>
          </div>
        </form>
      </div>
    </Card>
  );
};

export default PolicyFormNew;